import { useEffect, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { ApiError } from '../lib/api';
import { copyText } from '../lib/clipboard';
import { errorFieldLabels } from '../lib/form-errors';
import { haptic, telegram } from '../lib/telegram';
import { CloseIcon } from './icons';

export function PageHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow?: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
      </div>
      {action}
    </header>
  );
}

export function Card({
  children,
  className = '',
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <section
      className={`card ${onClick ? 'card--interactive' : ''} ${className}`}
      onClick={onClick}
    >
      {children}
    </section>
  );
}

export function Button({
  variant = 'primary',
  wide,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  wide?: boolean;
}) {
  return (
    <button
      {...props}
      className={`button button--${variant} ${wide ? 'button--wide' : ''} ${props.className ?? ''}`}
    />
  );
}

export function StatusBadge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';
}) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className={`field ${error ? 'field--invalid' : ''}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && !error && <span className="field__hint">{hint}</span>}
      {error && <span className="field__error">{error}</span>}
    </label>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input ${props.className ?? ''}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`input ${props.className ?? ''}`} />;
}

const COPY_RESET_DELAY_MS = 2400;

/**
 * Ссылка на публичную страницу с копированием в один тап. Результат виден
 * сразу: подпись меняется, рамка подсвечивается, в Telegram срабатывает
 * вибрация. Если скопировать не удалось, ссылка остаётся выделяемой руками.
 */
export function CopyLink({ url }: { url: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') {
      return;
    }

    const timer = setTimeout(() => setState('idle'), COPY_RESET_DELAY_MS);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <button
      type="button"
      className={`public-link ${state === 'idle' ? '' : `is-${state}`}`}
      onClick={() => {
        void copyText(url).then((copied) => {
          setState(copied ? 'copied' : 'failed');
          haptic(copied ? 'success' : 'error');
        });
      }}
    >
      <span>{url}</span>
      <strong aria-live="polite">
        {state === 'copied'
          ? 'Скопировано ✓'
          : state === 'failed'
            ? 'Скопируйте вручную'
            : 'Копировать'}
      </strong>
    </button>
  );
}

export function EmptyState({
  title,
  text,
  action,
}: {
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__mark">✦</div>
      <h2>{title}</h2>
      <p>{text}</p>
      {action}
    </div>
  );
}

export function LoadingScreen({ label = 'Загружаем ArtFlow' }: { label?: string }) {
  return (
    <div className="loading-screen">
      <div className="spinner" />
      <p>{label}</p>
    </div>
  );
}

/**
 * `labels` перечисляет русские названия полей формы. Тогда к общему тексту
 * ошибки добавляется список полей, в которых она возникла: без него пользователь
 * видит «Проверьте заполненные поля» и не понимает, где именно ошибся.
 */
export function ErrorNotice({
  error,
  labels,
}: {
  error: unknown;
  labels?: Record<string, string>;
}) {
  const message = error instanceof ApiError ? error.message : 'Что-то пошло не так';
  const fields = labels ? errorFieldLabels(error, labels) : [];

  return (
    <div className="notice notice--error">
      <span>{message}</span>
      {fields.length > 0 && <span className="notice__fields">Проверьте: {fields.join(', ')}</span>}
    </div>
  );
}

export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  /**
   * Пока лист открыт, страница под ним не прокручивается. Иначе браузер
   * подкручивает и лист, и страницу разом — при появлении ошибки окно
   * несколько раз дёргается вверх-вниз.
   */
  useEffect(() => {
    document.documentElement.classList.add('is-modal-open');
    // Если лист открыли в частично свёрнутом Mini App, сразу возвращаем
    // рабочую высоту — иначе кнопки формы остаются ниже видимой области.
    telegram()?.expand();
    return () => document.documentElement.classList.remove('is-modal-open');
  }, []);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal__handle" />
        <div className="modal__header">
          <h2>{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            <CloseIcon />
          </button>
        </div>
        {children}
      </section>
    </div>
  );
}
